import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { mapEntity } from '@api/mappers/mapEntity';
import { mapValueTypeToFormatter } from '@api/utils/watchlist-table-formatters';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { WithNAFallback } from '@design/components/table';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { isNonZeroReturn } from '@helpers/utils/asset-price/asset-price';
import { NumericalCellValueStyle } from '@helpers/utils/table/table';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { PriceCellTooltip } from '@terminal/components/price-cell/price-cell-tooltip/PriceCellTooltip';
import { getPriceStamp } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { config } from '@terminal/config/app.config';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useOneDayReturnLivePrice } from '@terminal/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CompetitorCard.module.scss';

const colors: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: 'var(--viz-bullish)',
  [NumericalCellValueStyle.Negative]: 'var(--viz-bearish)',
  [NumericalCellValueStyle.Neutral]: 'var(--text-default)',
};

const backgroundColors: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: 'var(--viz-bullish-soft)',
  [NumericalCellValueStyle.Negative]: 'var(--viz-bearish-soft)',
  [NumericalCellValueStyle.Neutral]: 'transparent',
};

interface CompetitorCardProps {
  entity: Entity;
  rank?: number;
  clickHandler?: () => void;
}

export const CompetitorCard = ({
  entity,
  rank,
  clickHandler,
}: CompetitorCardProps) => {
  const { t } = useTranslation(['widget']);
  const {
    oneDayLiveReturn,
    isSubscribing,
    date,
    isLive,
    isLivePriceSupported,
    isExtendedHours,
  } = useOneDayReturnLivePrice(mapEntity(entity));
  const stamp = getPriceStamp({
    isLivePriceSupported,
    isLive,
    isDisabled: false,
  });

  const { navigateToPage } = useNavigateToAssetOverview();

  const handleClick = () => {
    if (clickHandler) {
      clickHandler();
    } else if (!config.app.isTeamsApp) {
      navigateToPage({
        entityTag: entity.tag,
        source: POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER,
      });
    }
  };

  const formatter = mapValueTypeToFormatter['float'];
  const isNegative = !!oneDayLiveReturn && oneDayLiveReturn < 0;
  const prefix = isNegative ? '' : '+';
  const numericalStyle =
    oneDayLiveReturn === undefined
      ? NumericalCellValueStyle.Neutral
      : isNegative
        ? NumericalCellValueStyle.Negative
        : NumericalCellValueStyle.Positive;

  if (isSubscribing) {
    return (
      <SkeletonLoader
        areas={[
          { styles: { width: '100%', height: '132px', borderRadius: '8px' } },
        ]}
      />
    );
  }

  return (
    <div
      className={styles.root}
      data-testid="competitor-card"
      onClick={handleClick}
    >
      <div className={styles.iconsWrapper}>
        <AssetLogoContainer entity={entity} />
        {rank && (
          <TopRankIcon
            rank={rank}
            iconSize={24}
            rankFontSize={10}
            rankTopPosition="28%"
          />
        )}
      </div>
      <div className={styles.flexColumn}>
        <div className={styles.titleLabel} data-testid={`${entity.tag}-label`}>
          {formatTicker(entity)}
        </div>
        <div className={styles.subtitleLabel}>{entity.name}</div>
      </div>
      <Tooltip
        isTouchDevice={false}
        trigger={TooltipTrigger.Hover}
        label={date && <PriceCellTooltip type={stamp.stampType} date={date} />}
        inPortal
        disabled={!date || !oneDayLiveReturn}
      >
        <div
          className={styles.changePill}
          style={
            {
              '--change-color': colors[numericalStyle],
              '--change-bg': backgroundColors[numericalStyle],
            } as CSSProperties
          }
        >
          <div className={styles.horizonLabel}>
            {t(
              'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.oneDay'
            )}
          </div>
          <div className={styles.changeLabel}>
            <WithNAFallback
              check={
                isExtendedHours
                  ? isNonZeroReturn(oneDayLiveReturn)
                  : oneDayLiveReturn !== undefined
              }
              value={
                oneDayLiveReturn !== undefined
                  ? `${prefix}${formatter(oneDayLiveReturn)}%`
                  : undefined
              }
            />
          </div>
        </div>
      </Tooltip>
    </div>
  );
};
